/**
 * The next step, above the composer (D349): for the claim the reader holds, the
 * one press its record asks for — "Why do these disagree?" over an open conflict,
 * the source check over a claim nothing has checked — with the record that put it
 * there under its name, and the press's figure on its button.
 *
 * WHICH STEP is `core/nextStep.ts`'s: a fixed order of rules over stored records,
 * none of which reads the model's own ratings or prose, and where none fires
 * this renders nothing. The step is data; this file is the button.
 *
 * THE SAME PRESS as the modes' (D347). A check, a survey and a prior-art search go
 * through `pressVerb`, so they join Up next under a pass in flight (D162) and
 * grey only where the line cannot take them; the comparison goes through
 * `pressDisagree`, which refuses under the lock in its own sentence and is greyed
 * there. Each is priced by the arithmetic its mode's button uses — an estimate
 * that leans high, never a cost (invariant 5) — and none runs until it is pressed
 * (invariant 9).
 *
 * IT STEPS ASIDE while its own press is already working on this claim, in flight
 * or waiting on the line: the feed and the line say so, and a second offer of the
 * same press would be an offer to do it twice. As results land it re-reads the
 * record, so a check that comes back moves it on to whatever is next, or away.
 *
 * The button says the act in one word and its figure — "Compare · est. +$0.30" —
 * and not "Run": in this app a run is the loop with a budget (`RunStrip`), and
 * this is one pass.
 */

import { useId, useMemo, type CSSProperties } from 'react'
import { resolveFor, useStore } from '../core/store'
import { nextStepFor, type NextStep, type NextStepKind } from '../core/nextStep'
import { upNextPriceVerb, upNextRole, upNextShut, type UpNextVerb } from '../core/upNext'
import { STATUS_WORD, pressEstimate } from '../core/vocabulary'
import { projectVerbUsd } from '../engine/run'
import { disagreeEstimate, disagreeRefusal, pressDisagree } from './disagreePress'
import { pressName, pressTail } from './pressName'
import { pressVerb } from './upNextPress'
import { useFigureBasis } from './useFigureBasis'
import { useVoiceOn } from './useVoiceOn'

/** The act in one word, on the button: the line above it names the whole press. */
const VERB_WORD: Record<NextStepKind, string> = {
  compare: 'Compare',
  survey: 'Survey',
  priorArt: 'Search',
  check: 'Check',
}

/** What the dispatcher is handed, and prices it by, for the steps that queue. */
const DISPATCH: Record<Exclude<NextStepKind, 'compare'>, UpNextVerb> = {
  survey: 'verify',
  priorArt: 'priorArt',
  check: 'verify',
}

/** The hue of what the step is about, as the canvas draws it. */
const TINT: Record<NextStepKind, string> = {
  compare: 'var(--sem-tension)',
  survey: 'var(--sem-signal)',
  priorArt: 'var(--sem-insight)',
  check: 'var(--sem-signal)',
}

/**
 * The next step for `nodeId`, or nothing. `style` is the composer's own ground for
 * the rows above its bar, so this row reads as one of them.
 */
export function NextStepCard({ nodeId, style }: { nodeId: string; style?: CSSProperties }) {
  const session = useStore((s) => s.session)
  const step = useMemo(() => nextStepFor(session, nodeId), [session, nodeId])
  const working = useStore((s) => {
    if (!step || step.kind === 'compare') return false
    const verb = DISPATCH[step.kind]
    return (
      (s.busy && s.activeTargets.includes(step.targetId)) ||
      s.upNext.some((item) => item.nodeId === step.targetId && item.verb === verb)
    )
  })
  const titleId = useId()
  const reasonId = useId()
  if (!step || working) return null
  return (
    <section
      className="nextstep"
      data-kind={step.kind}
      aria-labelledby={titleId}
      // The step's hue, for the square before its name (D352). It edged the
      // whole card until the thread's cards went neutral.
      style={{ ...style, '--step-tint': TINT[step.kind] } as CSSProperties}
    >
      <div className="nextstep-text">
        <b id={titleId}>{step.title}</b>
        <span id={reasonId} title={step.reason}>
          {step.reason}
        </span>
      </div>
      {step.kind === 'compare' ? (
        <ComparePress step={step} describedBy={reasonId} />
      ) : (
        <VerbPress step={step} verb={DISPATCH[step.kind]} describedBy={reasonId} />
      )}
    </section>
  )
}

/** A check, a survey or a prior-art search: `pressVerb`, priced on its route. */
function VerbPress({
  step,
  verb,
  describedBy,
}: {
  step: NextStep
  verb: UpNextVerb
  describedBy: string
}) {
  const shut = useStore((s) => upNextShut(s, verb))
  // Over the route the pass runs on, never the selection (invariant 4, D202).
  const ready = useStore((s) => resolveFor(s, upNextRole(verb)) !== null)
  const est = useStore((s) =>
    projectVerbUsd({ modelId: s.modelId, roleModels: s.roleModels }, upNextPriceVerb(verb)),
  )
  // Its figure includes the voice's line while the voice is on (D295).
  useVoiceOn()
  useFigureBasis()
  return (
    <button
      type="button"
      className="btn nextstep-go"
      // A full Up next line shuts it without taking it out of the tab order;
      // the press path says why, as the mode's button does.
      aria-disabled={shut || undefined}
      aria-describedby={describedBy}
      onClick={() => pressVerb(verb, step.targetId)}
    >
      {pressName(VERB_WORD[step.kind], pressTail({ ready, est }))}
    </button>
  )
}

/** "Why do these disagree?": `pressDisagree`, which refuses under the lock. */
function ComparePress({ step, describedBy }: { step: NextStep; describedBy: string }) {
  const busy = useStore((s) => s.busy)
  const run = useStore((s) => s.run)
  const session = useStore((s) => s.session)
  const modelId = useStore((s) => s.modelId)
  const keys = useStore((s) => s.keys)
  const customBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  const view = { busy, run, session, modelId, keys, customBase, customModel }
  const refusal = disagreeRefusal(view, step.targetId)
  const locked = busy || (run !== null && run.phase !== 'ended')
  const keyless = !locked && refusal !== null
  const est = disagreeEstimate({ modelId })
  const tail = keyless
    ? STATUS_WORD.needsKey.toLowerCase()
    : est !== null
      ? pressEstimate(est)
      : null
  return (
    <button
      type="button"
      className="btn nextstep-go"
      aria-disabled={locked || undefined}
      aria-describedby={describedBy}
      title={refusal ?? undefined}
      onClick={() => void pressDisagree(step.targetId)}
    >
      {pressName(VERB_WORD.compare, tail)}
    </button>
  )
}
